import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { SelectableSettings } from '@progress/kendo-angular-grid';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Configuration } from '@shared/models/configuration';
import { ParticleService } from '@core/services/particle.service';
import { SmartGridService } from '@core/services/smart-grid.service';
import { InvalidateSmartGridAction } from '@shared/store/actions/smart-grid.actions';
import { AddFolderModel, FileManagerLocation } from '../../models/file-manager.model';
import { AddFolderAction, SetCurrentLocationAction, UploadFileByParentAction } from '../../store/actions/file-manager.actions';
import { FileManagerState } from '../../store/reducers/file-manager.reducer';
import { getCurrentLocation } from '../../store/selectors/file-manager.selectors';
import { FileNameCellViewerComponent } from '../cell-components/file-name-cell-viewer/file-name-cell-viewer.component';
import { AddFolderDialogComponent } from '../dialog-components/add-folder-dialog/add-folder-dialog.component';
import { UploadFileDialogComponent } from '../dialog-components/upload-file-dialog/upload-file-dialog.component';


@Component({
    standalone: false,
    selector: 'particle-file-manager-grid',
  templateUrl: './file-manager-grid.component.html',
  styleUrls: ['./file-manager-grid.component.scss']
})
export class FileManagerGridComponent implements OnInit, OnDestroy {
  @Input() sourceType: string;
  selectableSettings: SelectableSettings = {
    enabled: false,
    checkboxOnly: false,
    mode: 'multiple'
  };
  currentLocation: FileManagerLocation;
  private ngUnsubscribe = new Subject<void>();

  constructor(protected smartGridService: SmartGridService,
              protected particleService: ParticleService,
              protected store: Store<FileManagerState>,
              protected dialog: MatDialog) {
    particleService.registerCellComponent('FileNameCellViewer', FileNameCellViewerComponent);
  }

  ngOnInit() {
    const self = this;
    this.store.dispatch(new SetCurrentLocationAction('root', 'Root', 'Root'));
    const newConfig = Configuration.getSmartGridConfig(this.sourceType);
    newConfig.ItemsUrlIndex = 0;
    newConfig.Parameters.PARENT = 'Root';
    Configuration.setSmartGridConfig(this.sourceType, newConfig);
    this.store.pipe(select(getCurrentLocation), takeUntil(this.ngUnsubscribe)).subscribe(currentLocation => {
      self.currentLocation = currentLocation;
    });
    this.smartGridService.initDataProvider(this.sourceType);
  }

  uploadFile() {
    const pId = this.currentLocation.parentIds[this.currentLocation.parentIds.length - 1];
    const dialogRef = this.dialog.open(UploadFileDialogComponent, {
      data: {
        parentId: pId
      },
      disableClose: true
    });

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: FormData) => {
      if (result) {
        this.store.dispatch(new UploadFileByParentAction(result, pId, this.currentLocation.fullRelativePath, this.sourceType, this.currentLocation.fileNames[this.currentLocation.fileNames.length - 1]));
      }
    });
  }

  addFolder() {
    const parentId = this.currentLocation.parentIds[this.currentLocation.parentIds.length - 1];
    const dialogRef = this.dialog.open(AddFolderDialogComponent, {
      disableClose: true
    });

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: string) => {
      if (result) {
        const folderModel = new AddFolderModel({
          name: result,
          parentId: parentId
        });
        this.store.dispatch(new AddFolderAction(folderModel, this.sourceType));
      }
    });
  }

  navigateToFolder(index: number) {
    const newConfig = Configuration.getSmartGridConfig(this.sourceType);
    newConfig.Parameters.PARENT = this.currentLocation.parentIds[index];
    Configuration.setSmartGridConfig(this.sourceType, newConfig);
    this.store.dispatch(new InvalidateSmartGridAction(this.sourceType, true, false, false));

    this.store.dispatch(new SetCurrentLocationAction(
      this.currentLocation.fullRelativePath,
      this.currentLocation.parentIds[index],
      this.currentLocation.fileNames[index]
    ));
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
